import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import type {
  BizumCheckoutResponse,
  MySubscriptionResponse,
  Payment,
  SellablePlansResponse,
  StripeCheckoutResponse,
} from '@/types/billing'

export function useSellablePlans() {
  return useQuery<SellablePlansResponse>({
    queryKey: ['billing', 'plans'],
    queryFn: async () => (await apiClient.get('/api/billing/plans')).data,
    staleTime: 60 * 60_000,
  })
}

export function useMyPayments(enabled: boolean) {
  return useQuery<{ payments: Payment[] }>({
    queryKey: ['billing', 'my-payments'],
    queryFn: async () => (await apiClient.get('/api/billing/my-payments')).data,
    enabled,
    staleTime: 10_000,
  })
}

export function useCheckoutBizum() {
  const queryClient = useQueryClient()
  return useMutation<BizumCheckoutResponse, unknown, { plan: string }>({
    mutationFn: async (payload) => (await apiClient.post('/api/billing/checkout/bizum', payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing', 'my-payments'] }),
  })
}

export function useCheckoutStripe() {
  const queryClient = useQueryClient()
  return useMutation<StripeCheckoutResponse, unknown, { plan: string }>({
    mutationFn: async (payload) => (await apiClient.post('/api/billing/checkout/stripe', payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing', 'my-payments'] }),
  })
}

export function useMySubscriptionDetail(enabled: boolean) {
  return useQuery<MySubscriptionResponse>({
    queryKey: ['billing', 'my-subscription'],
    queryFn: async () => (await apiClient.get('/api/billing/my-subscription')).data,
    enabled,
    staleTime: 30_000,
  })
}

export function useStripePortal() {
  return useMutation<{ portal_url: string }, unknown, void>({
    mutationFn: async () => (await apiClient.post('/api/billing/portal/stripe')).data,
  })
}

export function useReportPayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, unknown, number>({
    mutationFn: async (paymentId) => (await apiClient.post(`/api/billing/payments/${paymentId}/report`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing', 'my-payments'] }),
  })
}

interface AdminPaymentsFilters {
  status?: string
  provider?: string
  plan?: string
}

export function useAdminPayments(enabled: boolean, filters: AdminPaymentsFilters = {}) {
  return useQuery<{ payments: Payment[] }>({
    queryKey: ['billing', 'admin', 'payments', filters],
    queryFn: async () => (await apiClient.get('/api/billing/admin/payments', { params: filters })).data,
    enabled,
    staleTime: 10_000,
  })
}

export function useApprovePayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, unknown, number>({
    mutationFn: async (paymentId) => (await apiClient.post(`/api/billing/admin/payments/${paymentId}/approve`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'admin', 'payments'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'admin', 'users'] })
    },
  })
}

export function useRejectPayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, unknown, { paymentId: number; notes?: string }>({
    mutationFn: async ({ paymentId, notes }) =>
      (await apiClient.post(`/api/billing/admin/payments/${paymentId}/reject`, { notes })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing', 'admin', 'payments'] }),
  })
}
